import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import React from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Button } from "./components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { 
  Calendar as CalendarIcon, 
  Clock, 
  MapPin, 
  Phone, 
  Mail, 
  CheckCircle2, 
  XCircle,
  ClipboardList,
  FileText,
  Menu,
  X,
  Sparkles,
  Shield,
  Zap,
  Award,
  Tag,
  Trash2,
  Plus,
  Building2,
  LogOut,
  User,
  Check
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hydrio Logo
const HYDRIO_LOGO = "https://customer-assets.emergentagent.com/job_wash-scheduler-29/artifacts/4uuso7ls_3e3d9e33-e8eb-49c0-acd7-9de1f65e3062.png";

// ==================== HEADER COMPONENT ====================
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  return (
    <header className="header" data-testid="main-header">
      <div className="header-content">
        <Link to="/" className="logo" data-testid="logo-link">
          <img src={HYDRIO_LOGO} alt="Hydrio" style={{ height: '56px', width: 'auto' }} />
        </Link>
        
        <nav className="nav-links">
          <a href="#services" className="nav-link">Uppfartstvätt</a>
          <a href="#garden" className="nav-link">Trädgård</a>
          <a href="#booking" className="nav-link">Boka Nu</a>
          <a href="#contact" className="nav-link">Kontakt</a>
          <Link to="/admin" className="nav-link">Admin</Link>
          <a href="#booking" className="nav-cta" data-testid="header-book-btn">Få Offert</a>
        </nav>

        <button 
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="mobile-menu" style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: 'white',
          padding: '1rem',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          <a href="#services" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Uppfartstvätt</a>
          <a href="#garden" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Trädgård</a>
          <a href="#booking" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Boka Nu</a>
          <a href="#contact" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Kontakt</a>
          <Link to="/admin" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Admin</Link>
          <a href="#booking" className="nav-cta" style={{ textAlign: 'center' }}>Få Offert</a>
        </div>
      )}
    </header>
  );
};

// ==================== HERO SECTION ====================
const HeroSection = () => {
  return (
    <section 
      className="hero" 
      style={{
        backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/106cfa46-b564-430a-915e-ebbb454beab9/images/3fb7e9192c5cd70aa90c84fc8f5a610db10eb1ae258bba3ad8bcd17883ab623e.png')`
      }}
      data-testid="hero-section"
    >
      <div className="hero-overlay"></div>
      <div className="hero-content">
        <h1>Rent. Enkelt. Hydrio.</h1>
        <p>Vi ger din uppfart nytt liv med professionell högtryckstvätt. Boka enkelt online och få ett skinande resultat.</p>
        <div className="hero-buttons">
          <a href="#booking" className="btn btn-primary" data-testid="hero-book-btn">
            <CalendarIcon size={20} />
            Boka Nu
          </a>
          <a href="#services" className="btn btn-outline" data-testid="hero-services-btn">
            Se Priser
          </a>
        </div>
      </div>
    </section>
  );
};

// ==================== BEFORE/AFTER SLIDER ====================
const BeforeAfterSlider = () => {
  const [sliderPosition, setSliderPosition] = useState(100);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = React.useRef(null);

  const beforeImage = "https://customer-assets.emergentagent.com/job_wash-scheduler-29/artifacts/wx3st6d7_IMG_9268.jpeg";
  const afterImage = "https://customer-assets.emergentagent.com/job_wash-scheduler-29/artifacts/z4xfn7h9_IMG_7735.jpeg";

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleMove(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging]);

  return (
    <section id="before-after" className="section" style={{ background: '#0f172a', padding: '4rem 0' }} data-testid="before-after-section">
      <div className="container">
        <div className="section-header" style={{ marginBottom: '2rem' }}>
          <span className="section-badge" style={{ background: '#10b981', color: 'white' }}>Se Skillnaden</span>
          <h2 className="section-title" style={{ color: 'white' }}>Före & Efter</h2>
          <p className="section-subtitle" style={{ color: '#94a3b8' }}>Dra i reglaget för att se den fantastiska skillnaden!</p>
        </div>

        <div 
          ref={containerRef}
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            aspectRatio: '4/3',
            borderRadius: '1rem',
            overflow: 'hidden',
            cursor: isDragging ? 'grabbing' : 'ew-resize',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            userSelect: 'none'
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          data-testid="before-after-slider"
        >
          {/* After Image (Background - Clean) */}
          <img 
            src={afterImage}
            alt="Efter - Ren uppfart"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            draggable={false}
          />

          {/* Before Image (Overlay - Dirty) */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
          }}>
            <img 
              src={beforeImage}
              alt="Före - Smutsig uppfart"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              draggable={false}
            />
          </div>

          {/* Slider Handle */}
          <div style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${sliderPosition}%`,
            transform: 'translateX(-50%)',
            width: '4px',
            background: 'white',
            boxShadow: '0 0 10px rgba(0,0,0,0.5)',
            zIndex: 10
          }}>
            {/* Handle Circle */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'white',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'grab'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2">
                <path d="M8 5l-5 7 5 7" />
                <path d="M16 5l5 7-5 7" />
              </svg>
            </div>
          </div>

          {/* Labels */}
          <div style={{
            position: 'absolute',
            top: '1rem',
            left: '1rem',
            background: 'rgba(239, 68, 68, 0.9)',
            color: 'white',
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            fontWeight: '700',
            fontSize: '0.875rem',
            letterSpacing: '0.05em',
            zIndex: 5,
            opacity: sliderPosition > 15 ? 1 : 0,
            transition: 'opacity 0.2s'
          }}>
            FÖRE
          </div>
          <div style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'rgba(16, 185, 129, 0.9)',
            color: 'white',
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            fontWeight: '700',
            fontSize: '0.875rem',
            letterSpacing: '0.05em',
            zIndex: 5,
            opacity: sliderPosition < 85 ? 1 : 0,
            transition: 'opacity 0.2s'
          }}>
            EFTER
          </div>
        </div>

        <p style={{ 
          textAlign: 'center', 
          color: '#64748b', 
          marginTop: '1.5rem',
          fontSize: '0.9375rem'
        }}>
          Dra reglaget åt vänster och höger för att se resultatet av vår högtryckstvätt
        </p>
      </div>
    </section>
  );
};

// ==================== LIVE TIME SLOTS SECTION ====================
const LiveTimeSlotsSection = () => {
  const [slots, setSlots] = useState([]);
  const [stats, setStats] = useState({ available: 0, booked: 0 });
  const [loading, setLoading] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    phone: '',
    address: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSlots();
    fetchStats();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchSlots();
      fetchStats();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSlots = async () => {
    try {
      const response = await axios.get(`${API}/timeslots`);
      setSlots(response.data);
    } catch (error) {
      console.error("Failed to fetch time slots:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/timeslots/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleSlotClick = (slot) => {
    setSelectedSlot(slot);
    setShowBookingModal(true);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!bookingForm.name || !bookingForm.phone || !bookingForm.address || !bookingForm.description) {
      toast.error("Fyll i alla fält");
      return;
    }

    setSubmitting(true);
    try {
      // Create a booking/quote request
      await axios.post(`${API}/quotes`, {
        name: bookingForm.name,
        phone: bookingForm.phone,
        address: bookingForm.address,
        message: `Önskad tid: ${selectedSlot.date} kl ${selectedSlot.time}\n\nBeskrivning: ${bookingForm.description}`
      });

      // Mark the slot as booked
      await axios.patch(`${API}/timeslots/${selectedSlot.id}/book?customer_name=${encodeURIComponent(bookingForm.name)}&service_type=${encodeURIComponent(bookingForm.description)}`);

      toast.success("Tack! Vi återkommer inom kort med pris och bekräftelse.");
      setShowBookingModal(false);
      setBookingForm({ name: '', phone: '', address: '', description: '' });
      setSelectedSlot(null);
      fetchSlots();
      fetchStats();
    } catch (error) {
      toast.error("Något gick fel. Försök igen eller ring oss.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'];
    const months = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
    return {
      dayName: days[date.getDay()],
      day: date.getDate(),
      month: months[date.getMonth()]
    };
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  const isTomorrow = (dateStr) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return dateStr === tomorrow.toISOString().split('T')[0];
  };

  // Group slots by date
  const groupedSlots = slots.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {});

  // Get first 5 dates
  const dates = Object.keys(groupedSlots).sort().slice(0, 5);

  if (loading) {
    return null;
  }

  if (slots.length === 0) {
    return null; // Don't show section if no slots available
  }

  return (
    <section id="live-slots" className="section" style={{ 
      background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
      paddingTop: '3rem',
      paddingBottom: '3rem'
    }} data-testid="live-slots-section">
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: '#ef4444',
            color: 'white',
            padding: '0.5rem 1rem',
            borderRadius: '2rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            marginBottom: '1rem'
          }}>
            <span style={{
              width: '8px',
              height: '8px',
              background: 'white',
              borderRadius: '50%',
              animation: 'pulse 2s infinite'
            }}></span>
            LEDIGA TIDER
          </div>
          <h2 style={{ fontSize: '2rem', fontWeight: '800', color: '#0f172a', marginBottom: '0.5rem' }}>
            Boka din tid nu!
          </h2>
          <p style={{ color: '#64748b', maxWidth: '500px', margin: '0 auto' }}>
            {stats.available > 0 ? (
              <>Endast <strong style={{ color: '#ef4444' }}>{stats.available} tider</strong> kvar denna vecka - först till kvarn!</>
            ) : (
              <>Alla tider är fullbokade just nu. Kontakta oss för specialtider!</>
            )}
          </p>
        </div>

        <div style={{
          display: 'flex',
          gap: '1rem',
          overflowX: 'auto',
          paddingBottom: '1rem',
          justifyContent: dates.length <= 3 ? 'center' : 'flex-start'
        }}>
          {dates.map((date) => {
            const dateInfo = formatDate(date);
            const daySlots = groupedSlots[date];
            const today = isToday(date);
            const tomorrow = isTomorrow(date);
            
            return (
              <div 
                key={date}
                style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '1.25rem',
                  minWidth: '160px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  border: today ? '2px solid #ef4444' : '1px solid #e2e8f0'
                }}
                data-testid={`slot-date-${date}`}
              >
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  {today && (
                    <span style={{
                      background: '#ef4444',
                      color: 'white',
                      fontSize: '0.625rem',
                      fontWeight: '700',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '1rem',
                      display: 'inline-block',
                      marginBottom: '0.25rem'
                    }}>IDAG</span>
                  )}
                  {tomorrow && !today && (
                    <span style={{
                      background: '#f97316',
                      color: 'white',
                      fontSize: '0.625rem',
                      fontWeight: '700',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '1rem',
                      display: 'inline-block',
                      marginBottom: '0.25rem'
                    }}>IMORGON</span>
                  )}
                  <p style={{ 
                    fontWeight: '600', 
                    color: '#64748b',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>{dateInfo.dayName}</p>
                  <p style={{ 
                    fontSize: '1.75rem', 
                    fontWeight: '800', 
                    color: '#0f172a',
                    lineHeight: 1
                  }}>{dateInfo.day}</p>
                  <p style={{ 
                    fontSize: '0.875rem', 
                    color: '#64748b',
                    textTransform: 'uppercase'
                  }}>{dateInfo.month}</p>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {daySlots.map((slot) => (
                    <button
                      key={slot.id}
                      onClick={() => handleSlotClick(slot)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        borderRadius: '0.5rem',
                        border: 'none',
                        background: '#10b981',
                        color: 'white',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.9375rem',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.25rem'
                      }}
                      className="slot-btn"
                      data-testid={`slot-btn-${slot.id}`}
                    >
                      <Clock size={14} />
                      {slot.time}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <style>{`
          .slot-btn:hover {
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedSlot && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setShowBookingModal(false)}
          data-testid="booking-modal-overlay"
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '1rem',
              padding: '2rem',
              maxWidth: '450px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="booking-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0f172a', marginBottom: '0.25rem' }}>
                  Boka tid
                </h3>
                <p style={{ color: '#10b981', fontWeight: '600' }}>
                  {formatDate(selectedSlot.date).dayName} {formatDate(selectedSlot.date).day} {formatDate(selectedSlot.date).month} kl {selectedSlot.time}
                </p>
              </div>
              <button 
                onClick={() => setShowBookingModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} color="#94a3b8" />
              </button>
            </div>

            <form onSubmit={handleBookingSubmit}>
              <div className="form-group">
                <label className="form-label">Namn *</label>
                <input
                  type="text"
                  className="form-input"
                  value={bookingForm.name}
                  onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                  placeholder="Ditt namn"
                  required
                  data-testid="modal-name-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Telefon *</label>
                <input
                  type="tel"
                  className="form-input"
                  value={bookingForm.phone}
                  onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                  placeholder="07X-XXX XX XX"
                  required
                  data-testid="modal-phone-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Adress *</label>
                <input
                  type="text"
                  className="form-input"
                  value={bookingForm.address}
                  onChange={(e) => setBookingForm({...bookingForm, address: e.target.value})}
                  placeholder="Gatuadress, Postnummer Ort"
                  required
                  data-testid="modal-address-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Vad ska göras? *</label>
                <textarea
                  className="form-textarea"
                  value={bookingForm.description}
                  onChange={(e) => setBookingForm({...bookingForm, description: e.target.value})}
                  placeholder="Beskriv vad du behöver hjälp med, t.ex. uppfartstvätt, ogräsrensning, altantvätt..."
                  rows={3}
                  required
                  data-testid="modal-description-input"
                />
              </div>

              <div style={{ 
                background: '#f0f9ff', 
                padding: '1rem', 
                borderRadius: '0.5rem', 
                marginBottom: '1.5rem',
                border: '1px solid #bae6fd'
              }}>
                <p style={{ color: '#0369a1', fontSize: '0.875rem' }}>
                  <strong>Hydrio återkommer inom kort</strong> med pris och bekräftelse på din bokning.
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={submitting}
                data-testid="modal-submit-btn"
              >
                {submitting ? 'Skickar...' : 'Skicka förfrågan'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

// ==================== SERVICES SECTION ====================
const ServicesSection = ({ services, onSelectService }) => {
  // Filter to only show driveway services
  const drivewayServices = services.filter(s => s.category === 'driveway' || !s.category);

  const handleBookService = (service) => {
    onSelectService(service);
    // Scroll to booking section
    setTimeout(() => {
      document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  
  return (
    <section id="services" className="section section-dark" data-testid="services-section">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">Uppfartstvätt</span>
          <h2 className="section-title">Välj ditt paket</h2>
          <p className="section-subtitle">Fasta priser utan dolda kostnader. Alltid nöjd-kund-garanti.</p>
        </div>
        
        <div className="services-grid" style={{ maxWidth: '800px', margin: '0 auto' }}>
          {drivewayServices.map((service) => (
            <div key={service.id} className="service-card" data-testid={`service-card-${service.id}`}>
              <img 
                src={service.image_url} 
                alt={service.name}
                className="service-image"
              />
              <div className="service-content">
                <h3 className="service-name">{service.name}</h3>
                <p className="service-description">{service.description}</p>
                <div className="service-meta">
                  <div className="service-duration">
                    <Clock size={16} />
                    ca {service.duration_minutes} min
                  </div>
                </div>
                <Button 
                  className="w-full mt-4 bg-sky-600 hover:bg-sky-700"
                  onClick={() => handleBookService(service)}
                  data-testid={`select-service-${service.id}`}
                >
                  Boka Nu
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== GARDEN SERVICES SECTION ====================
const GardenServicesSection = ({ services, onSelectService }) => {
  // Filter to only show garden services
  const gardenServices = services.filter(s => s.category === 'garden');
  
  if (gardenServices.length === 0) return null;

  const handleBookService = (service) => {
    onSelectService(service);
    // Scroll to booking section
    setTimeout(() => {
      document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  
  return (
    <section id="garden" className="section" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' }} data-testid="garden-services-section">
      <div className="container">
        <div className="section-header">
          <span className="section-badge" style={{ background: '#10b981', color: 'white' }}>Trädgårdstjänster</span>
          <h2 className="section-title">Trädgårdshjälp</h2>
          <p className="section-subtitle">Låt oss ta hand om din trädgård. Professionell service till fasta priser.</p>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '1.5rem',
          maxWidth: '1000px', 
          margin: '0 auto' 
        }}>
          {gardenServices.map((service) => (
            <div 
              key={service.id} 
              style={{
                background: 'white',
                borderRadius: '1rem',
                overflow: 'hidden',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              className="service-card"
              data-testid={`garden-service-card-${service.id}`}
            >
              <img 
                src={service.image_url} 
                alt={service.name}
                style={{
                  width: '100%',
                  height: '180px',
                  objectFit: 'cover'
                }}
              />
              <div style={{ padding: '1.5rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.5rem', color: '#0f172a' }}>
                  {service.name}
                </h3>
                <p style={{ color: '#64748b', fontSize: '0.9375rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                  {service.description}
                </p>
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleBookService(service)}
                  data-testid={`select-garden-service-${service.id}`}
                >
                  Boka Nu
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== PRICE CALCULATOR ====================
const PriceCalculator = () => {
  const [size, setSize] = useState("small");
  const [condition, setCondition] = useState("normal");
  const [calculatedPrice, setCalculatedPrice] = useState(null);

  const calculatePrice = () => {
    let basePrice = 399;
    
    // Size multiplier
    const sizeMultiplier = {
      small: 1,      // Under 30 kvm
      medium: 1.3,   // 30-60 kvm
      large: 1.6,    // 60-100 kvm
      xlarge: 2      // Över 100 kvm
    };
    
    // Condition multiplier
    const conditionMultiplier = {
      normal: 1,     // Normal smuts
      dirty: 1.25,   // Kraftigt smutsig
      stains: 1.5    // Oljefläckar/svåra fläckar
    };
    
    const price = Math.round(basePrice * sizeMultiplier[size] * conditionMultiplier[condition]);
    setCalculatedPrice(price);
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '1rem',
      padding: '1.5rem',
      border: '1px solid var(--border)',
      maxWidth: '400px',
      margin: '0 auto'
    }}>
      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', textAlign: 'center' }}>
        Få pris direkt
      </h3>
      
      <div className="form-group">
        <label className="form-label">Uppfartens storlek</label>
        <Select value={size} onValueChange={setSize}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="small">Liten (under 30 kvm)</SelectItem>
            <SelectItem value="medium">Medium (30-60 kvm)</SelectItem>
            <SelectItem value="large">Stor (60-100 kvm)</SelectItem>
            <SelectItem value="xlarge">Extra stor (över 100 kvm)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="form-group">
        <label className="form-label">Skick</label>
        <Select value={condition} onValueChange={setCondition}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="normal">Normal smuts & mossa</SelectItem>
            <SelectItem value="dirty">Kraftigt smutsig</SelectItem>
            <SelectItem value="stains">Oljefläckar / svåra fläckar</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <Button 
        onClick={calculatePrice}
        className="w-full bg-sky-600 hover:bg-sky-700 mb-4"
      >
        Beräkna pris
      </Button>
      
      {calculatedPrice && (
        <div style={{
          background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
          borderRadius: '0.75rem',
          padding: '1.25rem',
          textAlign: 'center',
          color: 'white'
        }}>
          <p style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.25rem' }}>Uppskattat pris</p>
          <p style={{ fontSize: '2.5rem', fontWeight: '700', fontFamily: 'Outfit, sans-serif' }}>
            {calculatedPrice} kr
          </p>
          <p style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.5rem' }}>
            * Slutpris kan variera efter besiktning
          </p>
        </div>
      )}
    </div>
  );
};

// ==================== BUSINESS WINDOW CLEANING SECTION ====================
const BusinessWindowSection = () => {
  return (
    <section id="foretag" style={{
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      padding: '5rem 1.5rem',
      position: 'relative',
      overflow: 'hidden'
    }} data-testid="business-section">
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '5%',
        width: '300px',
        height: '300px',
        background: 'rgba(2, 132, 199, 0.1)',
        borderRadius: '50%',
        filter: 'blur(60px)'
      }} />
      
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '3rem',
          alignItems: 'center'
        }}>
          <div>
            <span style={{
              display: 'inline-block',
              background: 'rgba(2, 132, 199, 0.2)',
              padding: '0.5rem 1rem',
              borderRadius: '9999px',
              color: '#38bdf8',
              fontSize: '0.875rem',
              fontWeight: '600',
              marginBottom: '1.5rem'
            }}>
              FÖR FÖRETAG
            </span>
            <h2 style={{ 
              color: 'white', 
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', 
              fontWeight: '700',
              marginBottom: '1rem',
              fontFamily: 'Outfit, sans-serif',
              lineHeight: '1.2'
            }}>
              Professionell Fönsterputs för Företag
            </h2>
            <p style={{ 
              color: 'rgba(255,255,255,0.7)', 
              fontSize: '1.125rem',
              marginBottom: '2rem',
              lineHeight: '1.7'
            }}>
              Skinande rena fönster ger ett professionellt intryck. Vi erbjuder pålitlig fönsterputs för kontor, butiker, restauranger och andra företagslokaler.
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircle2 size={18} color="#10b981" />
                </div>
                <span style={{ color: 'white', fontWeight: '500' }}>Gratis offert</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircle2 size={18} color="#10b981" />
                </div>
                <span style={{ color: 'white', fontWeight: '500' }}>Första jobbet gratis</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircle2 size={18} color="#10b981" />
                </div>
                <span style={{ color: 'white', fontWeight: '500' }}>Nöjdhetsgaranti</span>
              </div>
            </div>

            <a 
              href="#booking" 
              className="btn btn-primary"
              style={{ 
                background: '#0284c7',
                padding: '1rem 2rem'
              }}
            >
              Begär gratis offert
            </a>
          </div>
          
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '1.5rem',
            padding: '2rem',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
              borderRadius: '1rem',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                PRENUMERATIONSTJÄNST
              </p>
              <p style={{ color: 'white', fontSize: '2.5rem', fontWeight: '800', fontFamily: 'Outfit, sans-serif' }}>
                249 kr<span style={{ fontSize: '1rem', fontWeight: '400' }}>/mån</span>
              </p>
            </div>
            
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                padding: '0.75rem 0',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.9)'
              }}>
                <CheckCircle2 size={18} color="#10b981" />
                Fönsterputs 2 gånger i månaden
              </li>
              <li style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                padding: '0.75rem 0',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.9)'
              }}>
                <CheckCircle2 size={18} color="#10b981" />
                Bindningstid: 1 år
              </li>
              <li style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                padding: '0.75rem 0',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.9)'
              }}>
                <CheckCircle2 size={18} color="#10b981" />
                Fast pris - inga överraskningar
              </li>
              <li style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                padding: '0.75rem 0',
                color: 'rgba(255,255,255,0.9)'
              }}>
                <CheckCircle2 size={18} color="#10b981" />
                Nöjdhetsgaranti ingår
              </li>
            </ul>
            
            <a 
              href="#booking" 
              style={{
                display: 'block',
                textAlign: 'center',
                marginTop: '1.5rem',
                padding: '1rem',
                background: 'white',
                color: '#0f172a',
                borderRadius: '0.75rem',
                fontWeight: '600',
                textDecoration: 'none',
                transition: 'transform 0.2s'
              }}
            >
              Kom igång idag
            </a>
            <Link 
              to="/foretag"
              style={{
                display: 'block',
                textAlign: 'center',
                marginTop: '0.75rem',
                padding: '0.75rem',
                background: 'transparent',
                color: 'rgba(255,255,255,0.8)',
                borderRadius: '0.75rem',
                fontWeight: '500',
                textDecoration: 'none',
                border: '1px solid rgba(255,255,255,0.3)',
                fontSize: '0.875rem'
              }}
            >
              Har du redan konto? Logga in
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== CAMPAIGN SECTION ====================
const CampaignSection = () => {
  return (
    <section style={{
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      padding: '3rem 1.5rem',
      position: 'relative',
      overflow: 'hidden'
    }} data-testid="campaign-section">
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-10%',
        width: '400px',
        height: '400px',
        background: 'rgba(255,255,255,0.1)',
        borderRadius: '50%'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-30%',
        left: '-5%',
        width: '300px',
        height: '300px',
        background: 'rgba(255,255,255,0.1)',
        borderRadius: '50%'
      }} />
      
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: '3rem',
          flexWrap: 'wrap',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '500px' }}>
            <span style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.2)',
              padding: '0.5rem 1rem',
              borderRadius: '9999px',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: '600',
              marginBottom: '1rem'
            }}>
              INTRODUKTIONSERBJUDANDE
            </span>
            <h2 style={{ 
              color: 'white', 
              fontSize: 'clamp(2rem, 5vw, 3rem)', 
              fontWeight: '800',
              marginBottom: '0.75rem',
              fontFamily: 'Outfit, sans-serif'
            }}>
              399 kr
            </h2>
            <p style={{ 
              color: 'rgba(255,255,255,0.95)', 
              fontSize: '1.25rem',
              marginBottom: '0.5rem',
              fontWeight: '600'
            }}>
              Oavsett uppfartens storlek!
            </p>
            <p style={{ 
              color: 'rgba(255,255,255,0.85)', 
              fontSize: '1rem',
              marginBottom: '1.5rem'
            }}>
              Gäller nya kunder. Passa på innan erbjudandet tar slut!
            </p>
            <a 
              href="#booking" 
              className="btn btn-secondary"
              style={{ 
                background: 'white', 
                color: '#d97706',
                fontWeight: '700',
                padding: '1rem 2rem'
              }}
            >
              Boka med kampanjpris
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== FEATURES SECTION ====================
const FeaturesSection = () => {
  const features = [
    {
      icon: <Sparkles size={28} />,
      title: "Proffsig Utrustning",
      description: "Vi använder professionella högtryckstvättar för ett perfekt resultat varje gång."
    },
    {
      icon: <Shield size={28} />,
      title: "Fullt Försäkrade",
      description: "Du kan känna dig trygg - vi har full ansvarsförsäkring."
    },
    {
      icon: <Zap size={28} />,
      title: "Snabb Bokning",
      description: "Boka online på under en minut. Vi återkommer samma dag."
    },
    {
      icon: <Award size={28} />,
      title: "Nöjd-Kund-Garanti",
      description: "Inte nöjd? Vi kommer tillbaka och fixar det utan extra kostnad."
    }
  ];

  return (
    <section className="section" data-testid="features-section">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">Varför Hydrio?</span>
          <h2 className="section-title">Vi gör det enkelt för dig</h2>
        </div>
        
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card" data-testid={`feature-card-${index}`}>
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== REVIEWS SECTION ====================
const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newReview, setNewReview] = useState({ customer_name: "", rating: 5, comment: "" });
  const [hoveredStar, setHoveredStar] = useState(0);

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/reviews/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch review stats:", error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!newReview.customer_name.trim() || !newReview.comment.trim()) {
      toast.error("Fyll i alla fält");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, newReview);
      toast.success("Tack för din recension!");
      setNewReview({ customer_name: "", rating: 5, comment: "" });
      setShowForm(false);
      fetchReviews();
      fetchStats();
    } catch (error) {
      toast.error("Kunde inte skicka recension");
    } finally {
      setLoading(false);
    }
  };

  const StarRating = ({ rating, interactive = false, onRate = null, size = 20 }) => {
    return (
      <div style={{ display: 'flex', gap: '2px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onRate && onRate(star)}
            onMouseEnter={() => interactive && setHoveredStar(star)}
            onMouseLeave={() => interactive && setHoveredStar(0)}
            style={{
              background: 'none',
              border: 'none',
              cursor: interactive ? 'pointer' : 'default',
              padding: 0,
              display: 'flex',
              alignItems: 'center'
            }}
            disabled={!interactive}
          >
            <svg
              width={size}
              height={size}
              viewBox="0 0 24 24"
              fill={(interactive ? (hoveredStar || newReview.rating) : rating) >= star ? '#f59e0b' : 'none'}
              stroke={(interactive ? (hoveredStar || newReview.rating) : rating) >= star ? '#f59e0b' : '#d1d5db'}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
        ))}
      </div>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <section id="reviews" className="section" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }} data-testid="reviews-section">
      <div className="container">
        <div className="section-header">
          <span className="section-badge" style={{ background: '#f59e0b', color: 'white' }}>Kundrecensioner</span>
          <h2 className="section-title">Vad våra kunder säger</h2>
          <p className="section-subtitle">Läs vad andra tycker om Hydrio - eller lämna din egen recension!</p>
        </div>

        {/* Stats Summary */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2rem',
          marginBottom: '2rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', fontWeight: '800', color: '#0f172a', fontFamily: 'Outfit, sans-serif' }}>
              {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '-'}
            </div>
            <StarRating rating={Math.round(stats.average_rating)} size={24} />
            <p style={{ color: '#64748b', marginTop: '0.5rem' }}>{stats.total_reviews} recensioner</p>
          </div>
        </div>

        {/* Write Review Button */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Button 
            onClick={() => setShowForm(!showForm)}
            className="bg-amber-500 hover:bg-amber-600"
            data-testid="write-review-btn"
          >
            {showForm ? 'Stäng' : 'Skriv en recension'}
          </Button>
        </div>

        {/* Review Form */}
        {showForm && (
          <div style={{
            maxWidth: '500px',
            margin: '0 auto 2rem',
            background: 'white',
            borderRadius: '1rem',
            padding: '1.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }} data-testid="review-form">
            <form onSubmit={handleSubmitReview}>
              <div className="form-group">
                <label className="form-label">Ditt namn *</label>
                <input
                  type="text"
                  className="form-input"
                  value={newReview.customer_name}
                  onChange={(e) => setNewReview({ ...newReview, customer_name: e.target.value })}
                  placeholder="T.ex. Anna S."
                  required
                  data-testid="review-name-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Betyg *</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <StarRating 
                    rating={newReview.rating} 
                    interactive={true} 
                    onRate={(rating) => setNewReview({ ...newReview, rating })}
                    size={32}
                  />
                  <span style={{ color: '#64748b', fontSize: '0.875rem' }}>
                    {newReview.rating} av 5 stjärnor
                  </span>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Din recension *</label>
                <textarea
                  className="form-textarea"
                  value={newReview.comment}
                  onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                  placeholder="Berätta om din upplevelse med Hydrio..."
                  rows={4}
                  required
                  data-testid="review-comment-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-amber-500 hover:bg-amber-600"
                disabled={loading}
                data-testid="submit-review-btn"
              >
                {loading ? 'Skickar...' : 'Skicka recension'}
              </Button>
            </form>
          </div>
        )}

        {/* Reviews Grid */}
        {reviews.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {reviews.slice(0, 6).map((review) => (
              <div
                key={review.id}
                style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
                data-testid={`review-card-${review.id}`}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                  <div>
                    <p style={{ fontWeight: '600', color: '#0f172a', marginBottom: '0.25rem' }}>{review.customer_name}</p>
                    <StarRating rating={review.rating} size={16} />
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                    {formatDate(review.created_at)}
                  </span>
                </div>
                <p style={{ color: '#475569', lineHeight: '1.6', fontSize: '0.9375rem' }}>
                  "{review.comment}"
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
            <p>Inga recensioner ännu. Bli den första att lämna en!</p>
          </div>
        )}
      </div>
    </section>
  );
};

// ==================== BOOKING SECTION ====================
const BookingSection = ({ services, selectedService, setSelectedService }) => {
  const [bookingForm, setBookingForm] = useState({
    service_id: selectedService?.id || "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    address: "",
    preferred_date: "",
    preferred_time: "",
    notes: "",
    discount_code: ""
  });
  const [discountStatus, setDiscountStatus] = useState(null);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    address: "",
    description: "",
    driveway_size: "small",
    has_stains: false
  });
  const [bookingDate, setBookingDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Swedish service names mapping
  const serviceTranslations = {
    "Standard Driveway Wash": "Standard Uppfartstvätt",
    "Deep Clean Package": "Djuprengöringspaket",
    "Driveway + Patio Combo": "Uppfart + Uteplats Kombo"
  };

  useEffect(() => {
    if (selectedService) {
      setBookingForm(prev => ({ ...prev, service_id: selectedService.id }));
    }
  }, [selectedService]);

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!bookingForm.service_id) {
      toast.error("Vänligen välj en tjänst");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/bookings`, {
        ...bookingForm,
        preferred_date: bookingDate ? format(bookingDate, "yyyy-MM-dd") : ""
      });
      
      toast.success("Bokning skickad! Vi kontaktar dig snart.");
      setBookingForm({
        service_id: "",
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        address: "",
        preferred_date: "",
        preferred_time: "",
        notes: "",
        discount_code: ""
      });
      setBookingDate(null);
      setSelectedService(null);
      setDiscountStatus(null);
      setDiscountInfo(null);
    } catch (error) {
      toast.error("Kunde inte skicka bokning. Försök igen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, quoteForm);
      toast.success("Offertförfrågan skickad! Vi återkommer inom 24 timmar.");
      setQuoteForm({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        address: "",
        description: "",
        driveway_size: "small",
        has_stains: false
      });
    } catch (error) {
      toast.error("Kunde inte skicka offertförfrågan. Försök igen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeSlots = [
    "08:00", "09:00", "10:00", "11:00",
    "12:00", "13:00", "14:00", "15:00", "16:00"
  ];

  const validateDiscountCode = async (code) => {
    if (!code.trim()) {
      setDiscountStatus(null);
      setDiscountInfo(null);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/discount-codes/validate/${code}`);
      setDiscountStatus("valid");
      setDiscountInfo(response.data);
    } catch (error) {
      setDiscountStatus("invalid");
      setDiscountInfo(null);
    }
  };

  return (
    <section id="booking" className="section booking-section" data-testid="booking-section">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">Kom Igång</span>
          <h2 className="section-title">Boka din tvätt</h2>
          <p className="section-subtitle">Välj ett paket och boka direkt, eller berätta om ditt projekt för en offert.</p>
        </div>
        
        <div className="booking-grid">
          {/* Booking Form */}
          <div className="booking-card" data-testid="booking-form-card">
            <h3 className="booking-card-title">
              <CalendarIcon size={24} className="text-sky-600" />
              Boka en Tjänst
            </h3>
            <p className="booking-card-description">Välj en tjänst och välj önskat datum och tid.</p>
            
            <form onSubmit={handleBookingSubmit}>
              <div className="form-group">
                <label className="form-label">Tjänst *</label>
                <Select 
                  value={bookingForm.service_id} 
                  onValueChange={(value) => setBookingForm({ ...bookingForm, service_id: value })}
                >
                  <SelectTrigger className="w-full" data-testid="service-select">
                    <SelectValue placeholder="Välj en tjänst" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {serviceTranslations[service.name] || service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Fullständigt Namn *</label>
                <input
                  type="text"
                  className="form-input"
                  value={bookingForm.customer_name}
                  onChange={(e) => setBookingForm({ ...bookingForm, customer_name: e.target.value })}
                  required
                  data-testid="booking-name-input"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">E-post *</label>
                  <input
                    type="email"
                    className="form-input"
                    value={bookingForm.customer_email}
                    onChange={(e) => setBookingForm({ ...bookingForm, customer_email: e.target.value })}
                    required
                    data-testid="booking-email-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefon *</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={bookingForm.customer_phone}
                    onChange={(e) => setBookingForm({ ...bookingForm, customer_phone: e.target.value })}
                    required
                    data-testid="booking-phone-input"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Adress *</label>
                <input
                  type="text"
                  className="form-input"
                  value={bookingForm.address}
                  onChange={(e) => setBookingForm({ ...bookingForm, address: e.target.value })}
                  placeholder="Ange din fastighetsadress"
                  required
                  data-testid="booking-address-input"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Önskat Datum *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="booking-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {bookingDate ? format(bookingDate, "d MMMM yyyy", { locale: sv }) : "Välj datum"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={bookingDate}
                        onSelect={setBookingDate}
                        disabled={(date) => date < new Date()}
                        locale={sv}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="form-group">
                  <label className="form-label">Önskad Tid *</label>
                  <Select 
                    value={bookingForm.preferred_time} 
                    onValueChange={(value) => setBookingForm({ ...bookingForm, preferred_time: value })}
                  >
                    <SelectTrigger className="w-full" data-testid="booking-time-select">
                      <SelectValue placeholder="Välj tid" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((time) => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Ytterligare Information</label>
                <textarea
                  className="form-textarea"
                  value={bookingForm.notes}
                  onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                  placeholder="Eventuella speciella önskemål eller detaljer..."
                  data-testid="booking-notes-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Rabattkod (valfritt)</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    className="form-input"
                    value={bookingForm.discount_code}
                    onChange={(e) => setBookingForm({ ...bookingForm, discount_code: e.target.value.toUpperCase() })}
                    placeholder="Ange rabattkod"
                    style={{ flex: 1 }}
                    data-testid="booking-discount-input"
                  />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => validateDiscountCode(bookingForm.discount_code)}
                  >
                    Använd
                  </Button>
                </div>
                {discountStatus === "valid" && (
                  <p style={{ color: 'var(--success)', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    ✓ Rabattkod giltig! {discountInfo.discount_type === "percentage" ? `${discountInfo.discount_value}% rabatt` : `${discountInfo.discount_value} kr rabatt`}
                  </p>
                )}
                {discountStatus === "invalid" && (
                  <p style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    ✗ Ogiltig rabattkod
                  </p>
                )}
              </div>
              
              <Button 
                type="submit" 
                className="btn btn-primary btn-full"
                disabled={isSubmitting}
                data-testid="booking-submit-btn"
              >
                {isSubmitting ? "Skickar..." : "Boka Tjänst"}
              </Button>
            </form>
          </div>
          
          {/* Quote Request Form */}
          <div className="booking-card" data-testid="quote-form-card">
            <h3 className="booking-card-title">
              <FileText size={24} className="text-amber-500" />
              Begär Offert
            </h3>
            <p className="booking-card-description">Behöver du en anpassad offert? Berätta om ditt projekt.</p>
            
            <form onSubmit={handleQuoteSubmit}>
              <div className="form-group">
                <label className="form-label">Fullständigt Namn *</label>
                <input
                  type="text"
                  className="form-input"
                  value={quoteForm.customer_name}
                  onChange={(e) => setQuoteForm({ ...quoteForm, customer_name: e.target.value })}
                  required
                  data-testid="quote-name-input"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">E-post *</label>
                  <input
                    type="email"
                    className="form-input"
                    value={quoteForm.customer_email}
                    onChange={(e) => setQuoteForm({ ...quoteForm, customer_email: e.target.value })}
                    required
                    data-testid="quote-email-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefon *</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={quoteForm.customer_phone}
                    onChange={(e) => setQuoteForm({ ...quoteForm, customer_phone: e.target.value })}
                    required
                    data-testid="quote-phone-input"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Adress *</label>
                <input
                  type="text"
                  className="form-input"
                  value={quoteForm.address}
                  onChange={(e) => setQuoteForm({ ...quoteForm, address: e.target.value })}
                  placeholder="Ange din fastighetsadress"
                  required
                  data-testid="quote-address-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Uppfartens Storlek *</label>
                <Select 
                  value={quoteForm.driveway_size} 
                  onValueChange={(value) => setQuoteForm({ ...quoteForm, driveway_size: value })}
                >
                  <SelectTrigger className="w-full" data-testid="quote-size-select">
                    <SelectValue placeholder="Välj storlek" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Liten (under 30 kvm)</SelectItem>
                    <SelectItem value="medium">Medium (30-60 kvm)</SelectItem>
                    <SelectItem value="large">Stor (60-100 kvm)</SelectItem>
                    <SelectItem value="extra-large">Extra Stor (över 100 kvm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Projektbeskrivning *</label>
                <textarea
                  className="form-textarea"
                  value={quoteForm.description}
                  onChange={(e) => setQuoteForm({ ...quoteForm, description: e.target.value })}
                  placeholder="Beskriv din uppfart och eventuella specifika problem..."
                  required
                  data-testid="quote-description-input"
                />
              </div>
              
              <div className="form-group">
                <label className="checkbox-group">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    checked={quoteForm.has_stains}
                    onChange={(e) => setQuoteForm({ ...quoteForm, has_stains: e.target.checked })}
                    data-testid="quote-stains-checkbox"
                  />
                  <span>Min uppfart har oljefläckar eller svåra märken</span>
                </label>
              </div>
              
              <Button 
                type="submit" 
                className="btn btn-primary btn-full"
                disabled={isSubmitting}
                data-testid="quote-submit-btn"
              >
                {isSubmitting ? "Skickar..." : "Begär Offert"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== CONTACT SECTION ====================
const ContactSection = () => {
  return (
    <section id="contact" className="section section-dark" data-testid="contact-section">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">Kontakta Oss</span>
          <h2 className="section-title">Har du frågor?</h2>
          <p className="section-subtitle">Ring eller skicka ett meddelande på telefonnumret nedanför!</p>
        </div>
        
        <div className="contact-content">
          <div className="contact-info">
            <div className="contact-item">
              <div className="contact-icon">
                <Phone size={24} />
              </div>
              <div>
                <p className="contact-label">Telefon</p>
                <p className="contact-value">072-022 87 22</p>
              </div>
            </div>
            
            <div className="contact-item">
              <div className="contact-icon">
                <Mail size={24} />
              </div>
              <div>
                <p className="contact-label">E-post</p>
                <p className="contact-value">info@hydrio.se</p>
              </div>
            </div>
            
            <div className="contact-item">
              <div className="contact-icon">
                <MapPin size={24} />
              </div>
              <div>
                <p className="contact-label">Serviceområde</p>
                <p className="contact-value">Borås med omnejd</p>
              </div>
            </div>
            
            <div className="contact-item">
              <div className="contact-icon">
                <Clock size={24} />
              </div>
              <div>
                <p className="contact-label">Öppettider</p>
                <p className="contact-value">Mån - Lör: 07:00 - 18:00</p>
              </div>
            </div>
          </div>
          
          <div 
            style={{
              backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/106cfa46-b564-430a-915e-ebbb454beab9/images/2c0ea559004f236af0ef5c3d17ad8b6150a0d310621649eb76643f60780759da.png')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '1.5rem',
              minHeight: '300px'
            }}
          />
        </div>
      </div>
    </section>
  );
};

// ==================== FOOTER ====================
const Footer = () => {
  return (
    <footer className="footer" data-testid="footer">
      <div className="footer-content">
        <div className="footer-brand">
          <div className="footer-logo">
            <img src={HYDRIO_LOGO} alt="Hydrio" style={{ height: '70px', width: 'auto' }} />
          </div>
          <p className="footer-description">
            Professionell högtryckstvätt av uppfarter. Vi återställer din uppfarts ursprungliga skönhet med våra expertlösningar.
          </p>
        </div>
        
        <div>
          <h4 className="footer-heading">Tjänster</h4>
          <ul className="footer-links">
            <li><a href="#services">Standard Tvätt</a></li>
            <li><a href="#services">Djuprengöring</a></li>
            <li><a href="#booking">Begär Offert</a></li>
          </ul>
        </div>
        
        <div>
          <h4 className="footer-heading">Trädgård</h4>
          <ul className="footer-links">
            <li><a href="#garden">Ogräsrensning</a></li>
            <li><a href="#garden">Buskklippning</a></li>
            <li><a href="#garden">Altantvätt</a></li>
          </ul>
        </div>
        
        <div>
          <h4 className="footer-heading">Kontakt</h4>
          <ul className="footer-links">
            <li>072-022 87 22</li>
            <li>info@hydrio.se</li>
            <li>Borås med omnejd</li>
          </ul>
        </div>
      </div>
      
      <div className="footer-bottom">
        <p>© {new Date().getFullYear()} Hydrio. Alla rättigheter förbehållna.</p>
      </div>
    </footer>
  );
};

// ==================== HOME PAGE ====================
const HomePage = () => {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error("Failed to fetch services:", error);
      toast.error("Kunde inte ladda tjänster");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectService = (service) => {
    setSelectedService(service);
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <HeroSection />
      <LiveTimeSlotsSection />
      <ReviewsSection />
      <ServicesSection services={services} onSelectService={handleSelectService} />
      <GardenServicesSection services={services} onSelectService={handleSelectService} />
      <BeforeAfterSlider />
      <FeaturesSection />
      <BookingSection 
        services={services} 
        selectedService={selectedService}
        setSelectedService={setSelectedService}
      />
      <ContactSection />
      <Footer />
    </>
  );
};

// ==================== ADMIN LOGIN ====================
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simple password check - you can change this password
    if (password === "Hydrio2026") {
      localStorage.setItem("hydrio_admin", "true");
      onLogin();
    } else {
      setError(true);
      setTimeout(() => setError(false), 3000);
    }
  };

  return (
    <div className="payment-page" data-testid="admin-login">
      <div className="payment-card">
        <img src={HYDRIO_LOGO} alt="Hydrio" style={{ height: '60px', margin: '0 auto 1.5rem' }} />
        <h2 className="payment-title">Admin Inloggning</h2>
        <p className="payment-message">Ange lösenord för att komma åt admin-panelen.</p>
        
        <form onSubmit={handleSubmit} style={{ marginTop: '1.5rem' }}>
          <input
            type="password"
            className="form-input"
            placeholder="Lösenord"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ marginBottom: '1rem' }}
            data-testid="admin-password-input"
          />
          {error && (
            <p style={{ color: 'var(--danger)', fontSize: '0.875rem', marginBottom: '1rem' }}>
              Fel lösenord. Försök igen.
            </p>
          )}
          <Button 
            type="submit"
            className="w-full bg-sky-600 hover:bg-sky-700"
            data-testid="admin-login-btn"
          >
            Logga in
          </Button>
        </form>
      </div>
    </div>
  );
};

// ==================== TIME SLOTS MANAGER ====================
const TimeSlotsManager = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedTimes, setSelectedTimes] = useState(["09:00", "10:00", "11:00", "13:00", "14:00", "15:00"]);
  const [singleDate, setSingleDate] = useState(null);
  const [singleTime, setSingleTime] = useState("09:00");

  const timeOptions = [
    "08:00", "09:00", "10:00", "11:00", "12:00", 
    "13:00", "14:00", "15:00", "16:00", "17:00"
  ];

  useEffect(() => {
    fetchSlots();
  }, []);

  const fetchSlots = async () => {
    try {
      const response = await axios.get(`${API}/timeslots?include_all=true`);
      setSlots(response.data);
    } catch (error) {
      console.error("Failed to fetch slots:", error);
      toast.error("Kunde inte hämta tider");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSingleSlot = async () => {
    if (!singleDate) {
      toast.error("Välj ett datum");
      return;
    }
    
    try {
      await axios.post(`${API}/timeslots`, {
        date: format(singleDate, "yyyy-MM-dd"),
        time: singleTime
      });
      toast.success("Tid tillagd!");
      fetchSlots();
      setSingleDate(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte lägga till tid");
    }
  };

  const handleBulkCreate = async () => {
    if (selectedDates.length === 0) {
      toast.error("Välj minst ett datum");
      return;
    }
    if (selectedTimes.length === 0) {
      toast.error("Välj minst en tid");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/timeslots/bulk`, {
        dates: selectedDates.map(d => format(d, "yyyy-MM-dd")),
        times: selectedTimes
      });
      toast.success(response.data.message);
      fetchSlots();
      setSelectedDates([]);
      setShowBulkForm(false);
    } catch (error) {
      toast.error("Kunde inte skapa tider");
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/timeslots/${slotId}`);
      toast.success("Tid borttagen");
      fetchSlots();
    } catch (error) {
      toast.error("Kunde inte ta bort tid");
    }
  };

  const handleToggleStatus = async (slot) => {
    const newStatus = slot.status === 'available' ? 'blocked' : 'available';
    try {
      await axios.patch(`${API}/timeslots/${slot.id}/status?status=${newStatus}`);
      toast.success("Status uppdaterad");
      fetchSlots();
    } catch (error) {
      toast.error("Kunde inte uppdatera status");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { class: "status-badge status-paid", text: "Ledig" },
      booked: { class: "status-badge status-completed", text: "Bokad" },
      blocked: { class: "status-badge status-cancelled", text: "Blockerad" }
    };
    const config = statusConfig[status] || { class: "status-badge", text: status };
    return <span className={config.class}>{config.text}</span>;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return format(date, "EEEE d MMM", { locale: sv });
  };

  // Group slots by date
  const groupedSlots = slots.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedSlots).sort();

  const stats = {
    available: slots.filter(s => s.status === 'available').length,
    booked: slots.filter(s => s.status === 'booked').length,
    blocked: slots.filter(s => s.status === 'blocked').length
  };

  return (
    <div data-testid="timeslots-manager">
      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="stat-card">
          <p className="stat-label">Lediga tider</p>
          <p className="stat-value success">{stats.available}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Bokade tider</p>
          <p className="stat-value primary">{stats.booked}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Blockerade</p>
          <p className="stat-value warning">{stats.blocked}</p>
        </div>
      </div>

      {/* Add Slots */}
      <div className="data-table" style={{ marginBottom: '1.5rem' }}>
        <div className="table-header">
          <h3 className="table-title">Lägg till tider</h3>
          <Button 
            variant="outline"
            onClick={() => setShowBulkForm(!showBulkForm)}
          >
            {showBulkForm ? 'Enkel tid' : 'Masslägg tider'}
          </Button>
        </div>
        
        <div style={{ padding: '1.5rem' }}>
          {!showBulkForm ? (
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Datum</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[180px] justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {singleDate ? format(singleDate, "d MMM yyyy", { locale: sv }) : "Välj datum"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={singleDate}
                      onSelect={setSingleDate}
                      disabled={(date) => date < new Date()}
                      locale={sv}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Tid</label>
                <Select value={singleTime} onValueChange={setSingleTime}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions.map((time) => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                onClick={handleAddSingleSlot}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="add-single-slot-btn"
              >
                <Plus size={18} /> Lägg till
              </Button>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                <div>
                  <label className="form-label" style={{ marginBottom: '0.5rem', display: 'block' }}>
                    Välj datum (klicka för att markera flera)
                  </label>
                  <Calendar
                    mode="multiple"
                    selected={selectedDates}
                    onSelect={setSelectedDates}
                    disabled={(date) => date < new Date()}
                    locale={sv}
                  />
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                    {selectedDates.length} datum valda
                  </p>
                </div>
                
                <div>
                  <label className="form-label" style={{ marginBottom: '0.5rem', display: 'block' }}>
                    Välj tider
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {timeOptions.map((time) => (
                      <button
                        key={time}
                        onClick={() => {
                          setSelectedTimes(prev => 
                            prev.includes(time) 
                              ? prev.filter(t => t !== time)
                              : [...prev, time]
                          );
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          borderRadius: '0.5rem',
                          border: '1px solid var(--border)',
                          background: selectedTimes.includes(time) ? '#0284c7' : 'white',
                          color: selectedTimes.includes(time) ? 'white' : 'var(--text)',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                    {selectedTimes.length} tider valda
                  </p>
                  
                  <Button 
                    onClick={handleBulkCreate}
                    className="bg-sky-600 hover:bg-sky-700 mt-4"
                    data-testid="bulk-create-slots-btn"
                  >
                    Skapa {selectedDates.length * selectedTimes.length} tider
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Slots List */}
      <div className="data-table">
        <div className="table-header">
          <h3 className="table-title">Alla tider</h3>
        </div>
        
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            Laddar...
          </div>
        ) : sortedDates.length === 0 ? (
          <div className="empty-state">
            <Clock className="empty-icon" />
            <p>Inga tider inlagda ännu</p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              Lägg till lediga tider ovan för att visa dem på hemsidan
            </p>
          </div>
        ) : (
          <div style={{ padding: '1rem' }}>
            {sortedDates.map((date) => (
              <div key={date} style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ 
                  fontSize: '1rem', 
                  fontWeight: '600', 
                  marginBottom: '0.75rem',
                  textTransform: 'capitalize',
                  color: 'var(--text)'
                }}>
                  {formatDate(date)}
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {groupedSlots[date].sort((a, b) => a.time.localeCompare(b.time)).map((slot) => (
                    <div 
                      key={slot.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: slot.status === 'available' ? '#ecfdf5' : 
                                   slot.status === 'booked' ? '#eff6ff' : '#fef2f2',
                        borderRadius: '0.5rem',
                        border: `1px solid ${slot.status === 'available' ? '#10b981' : 
                                            slot.status === 'booked' ? '#3b82f6' : '#ef4444'}`
                      }}
                      data-testid={`slot-item-${slot.id}`}
                    >
                      <span style={{ fontWeight: '600' }}>{slot.time}</span>
                      {getStatusBadge(slot.status)}
                      {slot.booked_by && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          ({slot.booked_by})
                        </span>
                      )}
                      <div style={{ display: 'flex', gap: '0.25rem', marginLeft: '0.5rem' }}>
                        {slot.status !== 'booked' && (
                          <button
                            onClick={() => handleToggleStatus(slot)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              borderRadius: '0.25rem',
                              display: 'flex'
                            }}
                            title={slot.status === 'available' ? 'Blockera' : 'Gör ledig'}
                          >
                            {slot.status === 'available' ? <X size={16} color="#ef4444" /> : <Check size={16} color="#10b981" />}
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteSlot(slot.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            borderRadius: '0.25rem',
                            display: 'flex'
                          }}
                          title="Ta bort"
                        >
                          <Trash2 size={16} color="#94a3b8" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ADMIN DASHBOARD ====================
const AdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [businessAccounts, setBusinessAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [newDiscount, setNewDiscount] = useState({ code: "", discount_type: "percentage", discount_value: "", max_uses: "" });
  const navigate = useNavigate();

  // Check if already logged in
  useEffect(() => {
    const adminAuth = localStorage.getItem("hydrio_admin");
    if (adminAuth === "true") {
      setIsLoggedIn(true);
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("hydrio_admin");
    setIsLoggedIn(false);
  };

  // Swedish translations
  const serviceTranslations = {
    "Standard Driveway Wash": "Standard Uppfartstvätt",
    "Deep Clean Package": "Djuprengöringspaket",
    "Driveway + Patio Combo": "Uppfart + Uteplats Kombo"
  };

  const statusTranslations = {
    pending: "Väntande",
    confirmed: "Bekräftad",
    paid: "Betald",
    completed: "Slutförd",
    cancelled: "Avbokad",
    quoted: "Offert skickad",
    accepted: "Accepterad",
    declined: "Avböjd"
  };

  const sizeTranslations = {
    small: "Liten",
    medium: "Medium",
    large: "Stor",
    "extra-large": "Extra Stor"
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn]);

  const fetchData = async () => {
    try {
      const [bookingsRes, quotesRes, statsRes, discountsRes, businessRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/quotes`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/discount-codes`),
        axios.get(`${API}/business`)
      ]);
      setBookings(bookingsRes.data);
      setQuotes(quotesRes.data);
      setStats(statsRes.data);
      setDiscountCodes(discountsRes.data);
      setBusinessAccounts(businessRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Kunde inte ladda data");
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status, price = null) => {
    try {
      const updateData = { status };
      if (price !== null) updateData.price = price;
      
      await axios.patch(`${API}/bookings/${bookingId}`, updateData);
      toast.success("Bokning uppdaterad");
      fetchData();
      setSelectedItem(null);
      setModalType(null);
    } catch (error) {
      toast.error("Kunde inte uppdatera bokning");
    }
  };

  const updateQuoteStatus = async (quoteId, status, quoted_price = null, admin_notes = null) => {
    try {
      const updateData = { status };
      if (quoted_price !== null) updateData.quoted_price = quoted_price;
      if (admin_notes !== null) updateData.admin_notes = admin_notes;
      
      await axios.patch(`${API}/quotes/${quoteId}`, updateData);
      toast.success("Offert uppdaterad");
      fetchData();
      setSelectedItem(null);
      setModalType(null);
    } catch (error) {
      toast.error("Kunde inte uppdatera offert");
    }
  };

  const createDiscountCode = async () => {
    if (!newDiscount.code || !newDiscount.discount_value) {
      toast.error("Fyll i kod och rabattvärde");
      return;
    }
    
    try {
      await axios.post(`${API}/discount-codes`, {
        code: newDiscount.code,
        discount_type: newDiscount.discount_type,
        discount_value: parseFloat(newDiscount.discount_value),
        max_uses: newDiscount.max_uses ? parseInt(newDiscount.max_uses) : null
      });
      toast.success("Rabattkod skapad!");
      setNewDiscount({ code: "", discount_type: "percentage", discount_value: "", max_uses: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa rabattkod");
    }
  };

  const deleteDiscountCode = async (codeId) => {
    try {
      await axios.delete(`${API}/discount-codes/${codeId}`);
      toast.success("Rabattkod borttagen");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte ta bort rabattkod");
    }
  };

  const toggleDiscountCode = async (codeId) => {
    try {
      await axios.patch(`${API}/discount-codes/${codeId}/toggle`);
      fetchData();
    } catch (error) {
      toast.error("Kunde inte ändra status");
    }
  };

  const toggleBusinessSubscription = async (businessId, active) => {
    try {
      await axios.patch(`${API}/business/${businessId}/subscription?active=${active}`);
      toast.success(active ? "Prenumeration aktiverad" : "Prenumeration inaktiverad");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte ändra prenumeration");
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: "status-badge status-pending",
      confirmed: "status-badge status-confirmed",
      paid: "status-badge status-paid",
      completed: "status-badge status-completed",
      cancelled: "status-badge status-cancelled",
      quoted: "status-badge status-quoted",
      accepted: "status-badge status-paid",
      declined: "status-badge status-cancelled"
    };
    return <span className={statusClasses[status] || "status-badge"}>{statusTranslations[status] || status}</span>;
  };

  if (!isLoggedIn) {
    return <AdminLogin onLogin={() => setIsLoggedIn(true)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-layout" data-testid="admin-dashboard">
      <aside className="admin-sidebar">
        <nav>
          <ul className="sidebar-nav">
            <li>
              <button
                className={`sidebar-link ${activeTab === "bookings" ? "active" : ""}`}
                onClick={() => setActiveTab("bookings")}
                data-testid="nav-bookings"
              >
                <ClipboardList size={20} />
                Bokningar
              </button>
            </li>
            <li>
              <button
                className={`sidebar-link ${activeTab === "quotes" ? "active" : ""}`}
                onClick={() => setActiveTab("quotes")}
                data-testid="nav-quotes"
              >
                <FileText size={20} />
                Offerter
              </button>
            </li>
            <li>
              <button
                className={`sidebar-link ${activeTab === "timeslots" ? "active" : ""}`}
                onClick={() => setActiveTab("timeslots")}
                data-testid="nav-timeslots"
              >
                <Clock size={20} />
                Lediga Tider
              </button>
            </li>
            <li>
              <button
                className={`sidebar-link ${activeTab === "calendar" ? "active" : ""}`}
                onClick={() => setActiveTab("calendar")}
                data-testid="nav-calendar"
              >
                <CalendarIcon size={20} />
                Kalender
              </button>
            </li>
            <li>
              <button
                className={`sidebar-link ${activeTab === "discounts" ? "active" : ""}`}
                onClick={() => setActiveTab("discounts")}
                data-testid="nav-discounts"
              >
                <Tag size={20} />
                Rabattkoder
              </button>
            </li>
            <li>
              <button
                className={`sidebar-link ${activeTab === "business" ? "active" : ""}`}
                onClick={() => setActiveTab("business")}
                data-testid="nav-business"
              >
                <Building2 size={20} />
                Företagskonton
              </button>
            </li>
            <li style={{ marginTop: '2rem' }}>
              <button
                className="sidebar-link"
                onClick={handleLogout}
                data-testid="nav-logout"
              >
                <X size={20} />
                Logga ut
              </button>
            </li>
          </ul>
        </nav>
      </aside>

      <main className="admin-content">
        <div className="admin-header">
          <h1 className="admin-title">Adminpanel</h1>
          <p className="admin-subtitle">Hantera dina bokningar och offerter</p>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card" data-testid="stat-total-bookings">
            <p className="stat-label">Totalt Bokningar</p>
            <p className="stat-value primary">{stats?.total_bookings || 0}</p>
          </div>
          <div className="stat-card" data-testid="stat-pending-bookings">
            <p className="stat-label">Väntande Bokningar</p>
            <p className="stat-value warning">{stats?.pending_bookings || 0}</p>
          </div>
          <div className="stat-card" data-testid="stat-pending-quotes">
            <p className="stat-label">Väntande Offerter</p>
            <p className="stat-value warning">{stats?.pending_quotes || 0}</p>
          </div>
          <div className="stat-card" data-testid="stat-revenue">
            <p className="stat-label">Total Intäkt</p>
            <p className="stat-value success">{stats?.total_revenue?.toFixed(2) || "0.00"} kr</p>
          </div>
        </div>

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div className="data-table" data-testid="bookings-table">
            <div className="table-header">
              <h3 className="table-title">Bokningar</h3>
            </div>
            {bookings.length === 0 ? (
              <div className="empty-state">
                <ClipboardList className="empty-icon" />
                <p>Inga bokningar ännu</p>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Kund</th>
                    <th>Tjänst</th>
                    <th>Datum & Tid</th>
                    <th>Pris</th>
                    <th>Status</th>
                    <th>Åtgärder</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id} data-testid={`booking-row-${booking.id}`}>
                      <td>
                        <div>
                          <p className="font-medium">{booking.customer_name}</p>
                          <p className="text-sm text-gray-500">{booking.customer_email}</p>
                        </div>
                      </td>
                      <td>{serviceTranslations[booking.service_name] || booking.service_name}</td>
                      <td>
                        <div>
                          <p>{booking.preferred_date}</p>
                          <p className="text-sm text-gray-500">{booking.preferred_time}</p>
                        </div>
                      </td>
                      <td>{booking.price?.toFixed(2) || "-"} kr</td>
                      <td>{getStatusBadge(booking.status)}</td>
                      <td>
                        <div className="flex gap-2">
                          {booking.status === "pending" && (
                            <button
                              className="action-btn primary"
                              onClick={() => updateBookingStatus(booking.id, "confirmed")}
                              data-testid={`confirm-booking-${booking.id}`}
                            >
                              Bekräfta
                            </button>
                          )}
                          {booking.status === "confirmed" && (
                            <button
                              className="action-btn"
                              onClick={() => updateBookingStatus(booking.id, "completed")}
                              data-testid={`complete-booking-${booking.id}`}
                            >
                              Slutför
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Quotes Tab */}
        {activeTab === "quotes" && (
          <div className="data-table" data-testid="quotes-table">
            <div className="table-header">
              <h3 className="table-title">Offertförfrågningar</h3>
            </div>
            {quotes.length === 0 ? (
              <div className="empty-state">
                <FileText className="empty-icon" />
                <p>Inga offertförfrågningar ännu</p>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Kund</th>
                    <th>Adress</th>
                    <th>Storlek</th>
                    <th>Fläckar</th>
                    <th>Offertpris</th>
                    <th>Status</th>
                    <th>Åtgärder</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((quote) => (
                    <tr key={quote.id} data-testid={`quote-row-${quote.id}`}>
                      <td>
                        <div>
                          <p className="font-medium">{quote.customer_name}</p>
                          <p className="text-sm text-gray-500">{quote.customer_email}</p>
                        </div>
                      </td>
                      <td className="max-w-xs truncate">{quote.address}</td>
                      <td className="capitalize">{sizeTranslations[quote.driveway_size] || quote.driveway_size}</td>
                      <td>{quote.has_stains ? "Ja" : "Nej"}</td>
                      <td>{quote.quoted_price ? `${quote.quoted_price.toFixed(2)} kr` : "-"}</td>
                      <td>{getStatusBadge(quote.status)}</td>
                      <td>
                        {quote.status === "pending" && (
                          <button
                            className="action-btn primary"
                            onClick={() => {
                              setSelectedItem(quote);
                              setModalType("quote");
                            }}
                            data-testid={`quote-action-${quote.id}`}
                          >
                            Lägg till Offert
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Quote Modal */}
        {modalType === "quote" && selectedItem && (
          <QuoteModal
            quote={selectedItem}
            onClose={() => {
              setSelectedItem(null);
              setModalType(null);
            }}
            onSubmit={updateQuoteStatus}
          />
        )}

        {/* Time Slots Tab */}
        {activeTab === "timeslots" && (
          <TimeSlotsManager />
        )}

        {/* Calendar Tab */}
        {activeTab === "calendar" && (
          <div data-testid="calendar-view">
            <div className="data-table" style={{ marginBottom: '1.5rem' }}>
              <div className="table-header">
                <h3 className="table-title">Kalendervy</h3>
              </div>
              <div style={{ padding: '1.5rem', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                <div>
                  <Calendar
                    mode="single"
                    selected={calendarDate}
                    onSelect={(date) => date && setCalendarDate(date)}
                    locale={sv}
                    modifiers={{
                      booked: bookings.map(b => new Date(b.preferred_date)).filter(d => !isNaN(d))
                    }}
                    modifiersStyles={{
                      booked: { 
                        backgroundColor: 'rgba(2, 132, 199, 0.2)', 
                        borderRadius: '50%',
                        fontWeight: 'bold'
                      }
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: '300px' }}>
                  <h4 style={{ marginBottom: '1rem', fontSize: '1.125rem', fontWeight: '600' }}>
                    Bokningar {format(calendarDate, "d MMMM yyyy", { locale: sv })}
                  </h4>
                  {bookings.filter(b => b.preferred_date === format(calendarDate, "yyyy-MM-dd")).length === 0 ? (
                    <p style={{ color: 'var(--text-muted)' }}>Inga bokningar denna dag</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {bookings
                        .filter(b => b.preferred_date === format(calendarDate, "yyyy-MM-dd"))
                        .map((booking) => (
                          <div 
                            key={booking.id} 
                            style={{
                              padding: '1rem',
                              background: 'var(--background)',
                              borderRadius: '0.75rem',
                              border: '1px solid var(--border)'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                              <div>
                                <p style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{booking.customer_name}</p>
                                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{booking.service_name}</p>
                              </div>
                              <span style={{ 
                                background: 'var(--primary)', 
                                color: 'white', 
                                padding: '0.25rem 0.75rem', 
                                borderRadius: '9999px',
                                fontSize: '0.875rem',
                                fontWeight: '500'
                              }}>
                                {booking.preferred_time}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                              <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                <MapPin size={14} /> {booking.address}
                              </p>
                              <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Phone size={14} /> {booking.customer_phone}
                              </p>
                            </div>
                            <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              {getStatusBadge(booking.status)}
                              <span style={{ fontWeight: '600', color: 'var(--primary)' }}>{booking.price} kr</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Discounts Tab */}
        {activeTab === "discounts" && (
          <div data-testid="discounts-view">
            <div className="data-table" style={{ marginBottom: '1.5rem' }}>
              <div className="table-header">
                <h3 className="table-title">Skapa ny rabattkod</h3>
              </div>
              <div style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: '1', minWidth: '150px' }}>
                    <label className="form-label">Kod</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newDiscount.code}
                      onChange={(e) => setNewDiscount({ ...newDiscount, code: e.target.value.toUpperCase() })}
                      placeholder="T.ex. SOMMAR20"
                      data-testid="new-discount-code"
                    />
                  </div>
                  <div style={{ minWidth: '150px' }}>
                    <label className="form-label">Typ</label>
                    <Select 
                      value={newDiscount.discount_type} 
                      onValueChange={(value) => setNewDiscount({ ...newDiscount, discount_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Procent (%)</SelectItem>
                        <SelectItem value="fixed">Fast belopp (kr)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div style={{ minWidth: '100px' }}>
                    <label className="form-label">Värde</label>
                    <input
                      type="number"
                      className="form-input"
                      value={newDiscount.discount_value}
                      onChange={(e) => setNewDiscount({ ...newDiscount, discount_value: e.target.value })}
                      placeholder={newDiscount.discount_type === "percentage" ? "20" : "50"}
                      data-testid="new-discount-value"
                    />
                  </div>
                  <div style={{ minWidth: '120px' }}>
                    <label className="form-label">Max användningar</label>
                    <input
                      type="number"
                      className="form-input"
                      value={newDiscount.max_uses}
                      onChange={(e) => setNewDiscount({ ...newDiscount, max_uses: e.target.value })}
                      placeholder="Obegränsat"
                      data-testid="new-discount-max"
                    />
                  </div>
                  <Button 
                    onClick={createDiscountCode}
                    className="bg-sky-600 hover:bg-sky-700"
                    data-testid="create-discount-btn"
                  >
                    <Plus size={18} /> Skapa
                  </Button>
                </div>
              </div>
            </div>

            <div className="data-table">
              <div className="table-header">
                <h3 className="table-title">Aktiva rabattkoder</h3>
              </div>
              {discountCodes.length === 0 ? (
                <div className="empty-state">
                  <Tag className="empty-icon" />
                  <p>Inga rabattkoder ännu</p>
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Kod</th>
                      <th>Rabatt</th>
                      <th>Användningar</th>
                      <th>Status</th>
                      <th>Åtgärder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {discountCodes.map((code) => (
                      <tr key={code.id} data-testid={`discount-row-${code.id}`}>
                        <td>
                          <span style={{ fontFamily: 'monospace', fontWeight: '600', fontSize: '1rem' }}>{code.code}</span>
                        </td>
                        <td>
                          {code.discount_type === "percentage" ? `${code.discount_value}%` : `${code.discount_value} kr`}
                        </td>
                        <td>
                          {code.uses} {code.max_uses ? `/ ${code.max_uses}` : '(obegränsat)'}
                        </td>
                        <td>
                          <span className={`status-badge ${code.active ? 'status-paid' : 'status-cancelled'}`}>
                            {code.active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              className="action-btn"
                              onClick={() => toggleDiscountCode(code.id)}
                            >
                              {code.active ? 'Inaktivera' : 'Aktivera'}
                            </button>
                            <button
                              className="action-btn"
                              onClick={() => deleteDiscountCode(code.id)}
                              style={{ color: 'var(--danger)' }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Business Accounts Tab */}
        {activeTab === "business" && (
          <div data-testid="business-view">
            <div className="data-table">
              <div className="table-header">
                <h3 className="table-title">Företagskonton</h3>
              </div>
              {businessAccounts.length === 0 ? (
                <div className="empty-state">
                  <Building2 className="empty-icon" />
                  <p>Inga företagskonton ännu</p>
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Företag</th>
                      <th>Kontaktperson</th>
                      <th>E-post</th>
                      <th>Telefon</th>
                      <th>Prenumeration</th>
                      <th>Åtgärder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businessAccounts.map((account) => (
                      <tr key={account.id} data-testid={`business-row-${account.id}`}>
                        <td>
                          <p style={{ fontWeight: '600' }}>{account.company_name}</p>
                          <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{account.address}</p>
                        </td>
                        <td>{account.contact_name}</td>
                        <td>{account.email}</td>
                        <td>{account.phone}</td>
                        <td>
                          <span className={`status-badge ${account.subscription_active ? 'status-paid' : 'status-pending'}`}>
                            {account.subscription_active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td>
                          <button
                            className={`action-btn ${account.subscription_active ? '' : 'primary'}`}
                            onClick={() => toggleBusinessSubscription(account.id, !account.subscription_active)}
                          >
                            {account.subscription_active ? 'Inaktivera' : 'Aktivera'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// ==================== QUOTE MODAL ====================
const QuoteModal = ({ quote, onClose, onSubmit }) => {
  const [price, setPrice] = useState("");
  const [notes, setNotes] = useState("");

  const sizeTranslations = {
    small: "Liten",
    medium: "Medium",
    large: "Stor",
    "extra-large": "Extra Stor"
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!price) {
      toast.error("Vänligen ange ett pris");
      return;
    }
    onSubmit(quote.id, "quoted", parseFloat(price), notes);
  };

  return (
    <div className="modal-overlay" data-testid="quote-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Lägg till Offert</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Kund: <span className="font-medium text-gray-900">{quote.customer_name}</span></p>
              <p className="text-sm text-gray-600 mb-1">Storlek: <span className="font-medium text-gray-900 capitalize">{sizeTranslations[quote.driveway_size] || quote.driveway_size}</span></p>
              <p className="text-sm text-gray-600 mb-1">Har Fläckar: <span className="font-medium text-gray-900">{quote.has_stains ? "Ja" : "Nej"}</span></p>
              <p className="text-sm text-gray-600">Beskrivning: <span className="font-medium text-gray-900">{quote.description}</span></p>
            </div>
            
            <div className="form-group">
              <label className="form-label">Offertpris (kr) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-input"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Ange pris"
                required
                data-testid="quote-price-input"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Anteckningar</label>
              <textarea
                className="form-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Lägg till anteckningar för denna offert..."
                data-testid="quote-notes-input"
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="action-btn" onClick={onClose}>
              Avbryt
            </button>
            <button type="submit" className="action-btn primary" data-testid="submit-quote-btn">
              Skicka Offert
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== PAYMENT SUCCESS PAGE ====================
const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          pollPaymentStatus();
        }, 2000);
      }
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <div className="payment-page" data-testid="payment-success-page">
      <div className="payment-card">
        {status === "checking" && (
          <>
            <div className="spinner mx-auto mb-4"></div>
            <h2 className="payment-title">Behandlar Betalning...</h2>
            <p className="payment-message">Vänligen vänta medan vi bekräftar din betalning.</p>
          </>
        )}
        
        {status === "success" && (
          <>
            <div className="payment-icon success">
              <CheckCircle2 size={48} />
            </div>
            <h2 className="payment-title">Betalning Genomförd!</h2>
            <p className="payment-message">Tack för din bokning. Vi skickar en bekräftelse via e-post inom kort.</p>
            <Button 
              className="bg-sky-600 hover:bg-sky-700"
              onClick={() => navigate("/")}
              data-testid="return-home-btn"
            >
              Tillbaka till Startsidan
            </Button>
          </>
        )}
        
        {(status === "error" || status === "timeout" || status === "expired") && (
          <>
            <div className="payment-icon cancel">
              <XCircle size={48} />
            </div>
            <h2 className="payment-title">Betalningsproblem</h2>
            <p className="payment-message">
              {status === "expired" 
                ? "Din betalningssession har gått ut. Försök igen."
                : "Vi kunde inte bekräfta din betalning. Kontakta support."}
            </p>
            <Button 
              className="bg-sky-600 hover:bg-sky-700"
              onClick={() => navigate("/")}
              data-testid="return-home-btn"
            >
              Tillbaka till Startsidan
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== PAYMENT CANCEL PAGE ====================
const PaymentCancel = () => {
  const navigate = useNavigate();

  return (
    <div className="payment-page" data-testid="payment-cancel-page">
      <div className="payment-card">
        <div className="payment-icon cancel">
          <XCircle size={48} />
        </div>
        <h2 className="payment-title">Betalning Avbruten</h2>
        <p className="payment-message">Din betalning avbröts. Ingen debitering har gjorts.</p>
        <Button 
          className="bg-sky-600 hover:bg-sky-700"
          onClick={() => navigate("/")}
          data-testid="return-home-btn"
        >
          Tillbaka till Startsidan
        </Button>
      </div>
    </div>
  );
};

// ==================== BUSINESS LOGIN PAGE ====================
const BusinessLoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [businessAccount, setBusinessAccount] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [cleanings, setCleanings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [newCleaning, setNewCleaning] = useState({ scheduled_date: "", scheduled_time: "09:00", notes: "" });
  const [cleaningDate, setCleaningDate] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    password: "",
    confirmPassword: ""
  });

  useEffect(() => {
    // Check if already logged in
    const savedAccount = localStorage.getItem("hydrio_business");
    if (savedAccount) {
      const account = JSON.parse(savedAccount);
      setBusinessAccount(account);
      fetchBusinessData(account.id);
    }
  }, []);

  const fetchBusinessData = async (businessId) => {
    try {
      const [cleaningsRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/business/${businessId}/cleanings`),
        axios.get(`${API}/business/${businessId}/invoices`)
      ]);
      setCleanings(cleaningsRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error("Failed to fetch business data:", error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/business/login`, loginForm);
      localStorage.setItem("hydrio_business", JSON.stringify(response.data));
      setBusinessAccount(response.data);
      fetchBusinessData(response.data.id);
      toast.success("Inloggad!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Inloggning misslyckades");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error("Lösenorden matchar inte");
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error("Lösenordet måste vara minst 6 tecken");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/business/register`, {
        company_name: registerForm.company_name,
        contact_name: registerForm.contact_name,
        email: registerForm.email,
        phone: registerForm.phone,
        address: registerForm.address,
        password: registerForm.password
      });
      toast.success("Konto skapat! Du kan nu logga in.");
      setIsLogin(true);
      setRegisterForm({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        address: "",
        password: "",
        confirmPassword: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registrering misslyckades");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("hydrio_business");
    setBusinessAccount(null);
    setCleanings([]);
    setInvoices([]);
    toast.success("Utloggad");
  };

  const handleBookCleaning = async (e) => {
    e.preventDefault();
    if (!cleaningDate) {
      toast.error("Välj ett datum");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/business/${businessAccount.id}/cleanings`, {
        scheduled_date: format(cleaningDate, "yyyy-MM-dd"),
        scheduled_time: newCleaning.scheduled_time,
        notes: newCleaning.notes
      });
      toast.success("Städning bokad!");
      fetchBusinessData(businessAccount.id);
      setNewCleaning({ scheduled_date: "", scheduled_time: "09:00", notes: "" });
      setCleaningDate(null);
      setActiveTab("cleanings");
    } catch (error) {
      toast.error("Kunde inte boka städning");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInfo = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.patch(`${API}/business/${businessAccount.id}`, editForm);
      const updatedAccount = { ...businessAccount, ...response.data };
      localStorage.setItem("hydrio_business", JSON.stringify(updatedAccount));
      setBusinessAccount(updatedAccount);
      setEditMode(false);
      toast.success("Uppgifter uppdaterade!");
    } catch (error) {
      toast.error("Kunde inte uppdatera uppgifter");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = () => {
    setEditForm({
      company_name: businessAccount.company_name,
      contact_name: businessAccount.contact_name,
      phone: businessAccount.phone,
      address: businessAccount.address
    });
    setEditMode(true);
  };

  const timeSlots = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00"];

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { class: "status-badge status-pending", text: "Schemalagd" },
      completed: { class: "status-badge status-completed", text: "Genomförd" },
      cancelled: { class: "status-badge status-cancelled", text: "Avbokad" },
      pending: { class: "status-badge status-pending", text: "Obetald" },
      paid: { class: "status-badge status-paid", text: "Betald" },
      overdue: { class: "status-badge status-cancelled", text: "Förfallen" }
    };
    const config = statusConfig[status] || { class: "status-badge", text: status };
    return <span className={config.class}>{config.text}</span>;
  };

  // If logged in, show dashboard
  if (businessAccount) {
    return (
      <div className="admin-layout" data-testid="business-dashboard">
        <aside className="admin-sidebar">
          <nav>
            <ul className="sidebar-nav">
              <li>
                <button
                  className={`sidebar-link ${activeTab === "dashboard" ? "active" : ""}`}
                  onClick={() => setActiveTab("dashboard")}
                  data-testid="nav-dashboard"
                >
                  <Building2 size={20} />
                  Översikt
                </button>
              </li>
              <li>
                <button
                  className={`sidebar-link ${activeTab === "cleanings" ? "active" : ""}`}
                  onClick={() => setActiveTab("cleanings")}
                  data-testid="nav-cleanings"
                >
                  <CalendarIcon size={20} />
                  Städningar
                </button>
              </li>
              <li>
                <button
                  className={`sidebar-link ${activeTab === "invoices" ? "active" : ""}`}
                  onClick={() => setActiveTab("invoices")}
                  data-testid="nav-invoices"
                >
                  <FileText size={20} />
                  Fakturor
                </button>
              </li>
              <li>
                <button
                  className={`sidebar-link ${activeTab === "book" ? "active" : ""}`}
                  onClick={() => setActiveTab("book")}
                  data-testid="nav-book"
                >
                  <Plus size={20} />
                  Boka Städning
                </button>
              </li>
              <li>
                <button
                  className={`sidebar-link ${activeTab === "settings" ? "active" : ""}`}
                  onClick={() => setActiveTab("settings")}
                  data-testid="nav-settings"
                >
                  <User size={20} />
                  Inställningar
                </button>
              </li>
              <li style={{ marginTop: '2rem' }}>
                <button
                  className="sidebar-link"
                  onClick={handleLogout}
                  data-testid="nav-logout"
                >
                  <LogOut size={20} />
                  Logga ut
                </button>
              </li>
            </ul>
          </nav>
        </aside>

        <main className="admin-content">
          <div className="admin-header">
            <h1 className="admin-title">{businessAccount.company_name}</h1>
            <p className="admin-subtitle">Företagsportal</p>
          </div>

          {/* Dashboard Tab */}
          {activeTab === "dashboard" && (
            <>
              <div className="stats-grid">
                <div className="stat-card" data-testid="stat-subscription">
                  <p className="stat-label">Prenumerationsstatus</p>
                  <p className={`stat-value ${businessAccount.subscription_active ? 'success' : 'warning'}`}>
                    {businessAccount.subscription_active ? 'Aktiv' : 'Inaktiv'}
                  </p>
                </div>
                <div className="stat-card" data-testid="stat-cleanings-month">
                  <p className="stat-label">Städningar denna månad</p>
                  <p className="stat-value primary">{cleanings.filter(c => c.status === 'completed').length} / 2</p>
                </div>
                <div className="stat-card" data-testid="stat-next-cleaning">
                  <p className="stat-label">Nästa städning</p>
                  <p className="stat-value">
                    {cleanings.find(c => c.status === 'scheduled')?.scheduled_date || 'Ej schemalagd'}
                  </p>
                </div>
                <div className="stat-card" data-testid="stat-unpaid">
                  <p className="stat-label">Obetalda fakturor</p>
                  <p className="stat-value warning">{invoices.filter(i => i.status === 'pending').length}</p>
                </div>
              </div>

              {/* Upcoming Cleanings */}
              <div className="data-table" style={{ marginTop: '1.5rem' }}>
                <div className="table-header">
                  <h3 className="table-title">Kommande städningar</h3>
                </div>
                {cleanings.filter(c => c.status === 'scheduled').length === 0 ? (
                  <div className="empty-state">
                    <CalendarIcon className="empty-icon" />
                    <p>Inga schemalagda städningar</p>
                    <Button 
                      className="mt-4 bg-sky-600 hover:bg-sky-700"
                      onClick={() => setActiveTab("book")}
                    >
                      Boka städning
                    </Button>
                  </div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th>Tid</th>
                        <th>Typ</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cleanings.filter(c => c.status === 'scheduled').slice(0, 5).map((cleaning) => (
                        <tr key={cleaning.id}>
                          <td>{cleaning.scheduled_date}</td>
                          <td>{cleaning.scheduled_time}</td>
                          <td>{cleaning.is_extra ? 'Extra' : 'Prenumeration'}</td>
                          <td>{getStatusBadge(cleaning.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {!businessAccount.subscription_active && (
                <div style={{
                  marginTop: '2rem',
                  padding: '2rem',
                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  borderRadius: '1rem',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                    Aktivera din prenumeration
                  </h3>
                  <p style={{ opacity: 0.9, marginBottom: '1rem' }}>
                    Kontakta oss på 072-022 87 22 för att aktivera din prenumeration!
                  </p>
                  <a href="tel:0720228722" className="btn btn-secondary" style={{ background: 'white', color: '#0284c7' }}>
                    Ring nu
                  </a>
                </div>
              )}
            </>
          )}

          {/* Cleanings Tab */}
          {activeTab === "cleanings" && (
            <div className="data-table" data-testid="cleanings-table">
              <div className="table-header">
                <h3 className="table-title">Alla städningar</h3>
                <Button 
                  className="bg-sky-600 hover:bg-sky-700"
                  onClick={() => setActiveTab("book")}
                >
                  <Plus size={18} /> Boka ny
                </Button>
              </div>
              {cleanings.length === 0 ? (
                <div className="empty-state">
                  <CalendarIcon className="empty-icon" />
                  <p>Ingen städhistorik ännu</p>
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Tid</th>
                      <th>Typ</th>
                      <th>Anteckningar</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cleanings.map((cleaning) => (
                      <tr key={cleaning.id} data-testid={`cleaning-row-${cleaning.id}`}>
                        <td>{cleaning.scheduled_date}</td>
                        <td>{cleaning.scheduled_time}</td>
                        <td>{cleaning.is_extra ? 'Extra städning' : 'Prenumeration'}</td>
                        <td>{cleaning.notes || '-'}</td>
                        <td>{getStatusBadge(cleaning.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Invoices Tab */}
          {activeTab === "invoices" && (
            <div className="data-table" data-testid="invoices-table">
              <div className="table-header">
                <h3 className="table-title">Fakturor</h3>
              </div>
              {invoices.length === 0 ? (
                <div className="empty-state">
                  <FileText className="empty-icon" />
                  <p>Inga fakturor ännu</p>
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Fakturanummer</th>
                      <th>Period</th>
                      <th>Belopp</th>
                      <th>Förfallodatum</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                        <td className="font-medium">{invoice.invoice_number}</td>
                        <td>{invoice.period}</td>
                        <td>{invoice.amount} kr</td>
                        <td>{invoice.due_date}</td>
                        <td>{getStatusBadge(invoice.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Book Cleaning Tab */}
          {activeTab === "book" && (
            <div className="data-table" data-testid="book-cleaning-form">
              <div className="table-header">
                <h3 className="table-title">Boka extra städning</h3>
              </div>
              <div style={{ padding: '1.5rem', maxWidth: '500px' }}>
                <form onSubmit={handleBookCleaning}>
                  <div className="form-group">
                    <label className="form-label">Välj datum *</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="cleaning-date-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {cleaningDate ? format(cleaningDate, "d MMMM yyyy", { locale: sv }) : "Välj datum"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={cleaningDate}
                          onSelect={setCleaningDate}
                          disabled={(date) => date < new Date()}
                          locale={sv}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Tid *</label>
                    <Select 
                      value={newCleaning.scheduled_time} 
                      onValueChange={(value) => setNewCleaning({ ...newCleaning, scheduled_time: value })}
                    >
                      <SelectTrigger className="w-full" data-testid="cleaning-time-select">
                        <SelectValue placeholder="Välj tid" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((time) => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Anteckningar (valfritt)</label>
                    <textarea
                      className="form-textarea"
                      value={newCleaning.notes}
                      onChange={(e) => setNewCleaning({ ...newCleaning, notes: e.target.value })}
                      placeholder="T.ex. speciella önskemål eller instruktioner..."
                      data-testid="cleaning-notes-input"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-sky-600 hover:bg-sky-700"
                    disabled={loading}
                    data-testid="book-cleaning-btn"
                  >
                    {loading ? "Bokar..." : "Boka städning"}
                  </Button>
                </form>
                
                <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                  * Extra städningar utöver prenumerationen debiteras separat.
                </p>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <div className="data-table" data-testid="settings-form">
              <div className="table-header">
                <h3 className="table-title">Företagsuppgifter</h3>
                {!editMode && (
                  <Button variant="outline" onClick={startEdit} data-testid="edit-info-btn">
                    Redigera
                  </Button>
                )}
              </div>
              <div style={{ padding: '1.5rem' }}>
                {editMode ? (
                  <form onSubmit={handleUpdateInfo}>
                    <div className="form-group">
                      <label className="form-label">Företagsnamn</label>
                      <input
                        type="text"
                        className="form-input"
                        value={editForm.company_name}
                        onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                        required
                        data-testid="edit-company-name"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Kontaktperson</label>
                      <input
                        type="text"
                        className="form-input"
                        value={editForm.contact_name}
                        onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                        required
                        data-testid="edit-contact-name"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Telefon</label>
                      <input
                        type="tel"
                        className="form-input"
                        value={editForm.phone}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        required
                        data-testid="edit-phone"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Adress</label>
                      <input
                        type="text"
                        className="form-input"
                        value={editForm.address}
                        onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                        required
                        data-testid="edit-address"
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <Button 
                        type="submit" 
                        className="bg-sky-600 hover:bg-sky-700"
                        disabled={loading}
                        data-testid="save-info-btn"
                      >
                        {loading ? "Sparar..." : "Spara ändringar"}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => setEditMode(false)}
                      >
                        Avbryt
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
                    <div>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Företagsnamn</p>
                      <p style={{ fontWeight: '600' }}>{businessAccount.company_name}</p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Kontaktperson</p>
                      <p style={{ fontWeight: '600' }}>{businessAccount.contact_name}</p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '0.25rem' }}>E-post</p>
                      <p style={{ fontWeight: '600' }}>{businessAccount.email}</p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Telefon</p>
                      <p style={{ fontWeight: '600' }}>{businessAccount.phone}</p>
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Adress</p>
                      <p style={{ fontWeight: '600' }}>{businessAccount.address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // Login/Register forms
  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh', background: 'var(--background)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: '450px', padding: '1.5rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: 'rgba(2, 132, 199, 0.1)',
            borderRadius: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem'
          }}>
            <Building2 size={32} color="#0284c7" />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.5rem' }}>
            Företagsportal
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            {isLogin ? 'Logga in för att hantera din prenumeration' : 'Skapa ett företagskonto'}
          </p>
        </div>

        <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', border: '1px solid var(--border)' }}>
          {isLogin ? (
            <form onSubmit={handleLogin} data-testid="business-login-form">
              <div className="form-group">
                <label className="form-label">E-post</label>
                <input
                  type="email"
                  className="form-input"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  required
                  data-testid="business-login-email"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Lösenord</label>
                <input
                  type="password"
                  className="form-input"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  required
                  data-testid="business-login-password"
                />
              </div>
              <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700" disabled={loading} data-testid="business-login-btn">
                {loading ? "Loggar in..." : "Logga in"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleRegister} data-testid="business-register-form">
              <div className="form-group">
                <label className="form-label">Företagsnamn *</label>
                <input
                  type="text"
                  className="form-input"
                  value={registerForm.company_name}
                  onChange={(e) => setRegisterForm({ ...registerForm, company_name: e.target.value })}
                  required
                  data-testid="business-register-company"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Kontaktperson *</label>
                <input
                  type="text"
                  className="form-input"
                  value={registerForm.contact_name}
                  onChange={(e) => setRegisterForm({ ...registerForm, contact_name: e.target.value })}
                  required
                  data-testid="business-register-contact"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">E-post *</label>
                  <input
                    type="email"
                    className="form-input"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    required
                    data-testid="business-register-email"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefon *</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={registerForm.phone}
                    onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                    required
                    data-testid="business-register-phone"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Adress *</label>
                <input
                  type="text"
                  className="form-input"
                  value={registerForm.address}
                  onChange={(e) => setRegisterForm({ ...registerForm, address: e.target.value })}
                  required
                  data-testid="business-register-address"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Lösenord *</label>
                  <input
                    type="password"
                    className="form-input"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    required
                    data-testid="business-register-password"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Bekräfta lösenord *</label>
                  <input
                    type="password"
                    className="form-input"
                    value={registerForm.confirmPassword}
                    onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                    required
                    data-testid="business-register-confirm"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700" disabled={loading} data-testid="business-register-btn">
                {loading ? "Registrerar..." : "Skapa konto"}
              </Button>
            </form>
          )}

          <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border)' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              {isLogin ? 'Har du inget konto?' : 'Har du redan ett konto?'}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--primary)',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginLeft: '0.5rem'
                }}
                data-testid="toggle-auth-mode"
              >
                {isLogin ? 'Registrera dig' : 'Logga in'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN APP ====================
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Header />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
